import { create } from 'zustand';
import { authApi } from '../services/endpoints';

/**
 * E1 — canonical Sheba auth store for the website.
 *
 * Replaces the legacy prototype's ad hoc localStorage('token'/'user') +
 * AuthModal-local-state pattern with the same cookie-session-backed model
 * frontend/customer/src/store/authStore.ts already uses — the website is
 * a browser client of the same canonical backend, not a separate auth
 * system. Zustand was chosen (a new dependency for this project, added
 * this phase) specifically to mirror that proven pattern exactly, rather
 * than inventing a parallel Context-based implementation that would
 * behave subtly differently.
 */

let _startupRefreshDone = false;
let _initInProgress = false;

export const useAuthStore = create((set, get) => ({
  user: null,
  initialized: false,
  mfaRequired: false,
  preMfaToken: null,
  requiresPasscode: false,

  initialize: async () => {
    if (get().initialized || _initInProgress) return;
    _initInProgress = true;
    try {
      if (!_startupRefreshDone) {
        _startupRefreshDone = true;
        try {
          await authApi.refresh();
        } catch {
          // No existing refresh cookie — proceed to /auth/me anyway.
        }
      }
      const user = await authApi.me();
      set({ user, initialized: true });
    } catch {
      set({ user: null, initialized: true });
    } finally {
      _initInProgress = false;
    }
  },

  register: async (email, password, name) => {
    const data = await authApi.register(email, password, name);
    set({ user: data.user, requiresPasscode: !!data.requires_passcode });
    return data;
  },

  login: async (email, password) => {
    const data = await authApi.login(email, password);
    if (data.mfa_required && data.pre_mfa_token) {
      set({ mfaRequired: true, preMfaToken: data.pre_mfa_token });
      return { mfaRequired: true };
    }
    set({ user: data.user, mfaRequired: false, preMfaToken: null });
    return { mfaRequired: false };
  },

  verifyMfa: async (code) => {
    const { preMfaToken } = get();
    if (!preMfaToken) throw new Error('No pending MFA session');
    const data = await authApi.verifyMfa(preMfaToken, code);
    set({ user: data.user, mfaRequired: false, preMfaToken: null });
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Best-effort — clear local session regardless.
    }
    get()._clearSession();
  },

  setUser: (user) => set({ user }),

  _clearSession: () => set({ user: null, mfaRequired: false, preMfaToken: null }),
}));

if (typeof window !== 'undefined') {
  window.addEventListener('sheba:unauthorized', () => {
    useAuthStore.getState()._clearSession();
  });
}
